(() => {
  'use strict';
  const controllers = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function rotation(root, advance, count, cleanupVisuals) {
    const abort = new AbortController();
    const signal = abort.signal;
    const toggle = root.querySelector('[data-autoplay-toggle]');
    const delay = Number(root.dataset.autoplayMs);
    let timer = null, hovered = false, inView = false;
    let paused = reduced.matches || !!window.Shopify?.designMode;
    const clear = () => { clearTimeout(timer); timer = null; };
    const update = () => {
      clear();
      if (toggle) {
        toggle.textContent = paused ? 'Reanudar' : 'Pausar';
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', `${paused ? 'Reanudar' : 'Pausar'} ${root.hasAttribute('data-am-slider') ? 'campañas' : 'opiniones'}`);
      }
      const focused = root.contains(document.activeElement) && document.activeElement !== toggle;
      if (paused || hovered || focused || !inView || document.hidden || count() < 2) return;
      timer = setTimeout(() => { advance(); update(); }, delay);
    };
    const pause = () => { paused = true; update(); };
    toggle?.addEventListener('click', () => { paused = !paused; update(); }, {signal});
    root.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { hovered = true; update(); } }, {signal});
    root.addEventListener('pointerleave', () => { hovered = false; update(); }, {signal});
    root.addEventListener('focusin', update, {signal});
    root.addEventListener('focusout', () => queueMicrotask(update), {signal});
    document.addEventListener('visibilitychange', update, {signal});
    reduced.addEventListener('change', () => { if (reduced.matches) paused = true; update(); }, {signal});
    const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; update(); }, {threshold:0.1});
    observer.observe(root);
    update();
    return {signal, reset:update, pause, destroy:() => { clear(); observer.disconnect(); abort.abort(); cleanupVisuals(); }};
  }

  function hero(root) {
    const slides = [...root.querySelectorAll('[data-am-slide]')];
    if (!slides.length) return () => {};
    const dots = [...root.querySelectorAll('[data-slide-to]')];
    const status = root.querySelector('[data-slide-status]');
    let active = 0, cleanupTimer = null, animations = [], touchX = null;
    const finish = () => {
      clearTimeout(cleanupTimer);
      animations.forEach(a => a.cancel()); animations = [];
      slides.forEach((slide, i) => {slide.hidden = i !== active; slide.style.zIndex = '';});
    };
    const show = (index, animate = true, manual = false) => {
      const next = (index + slides.length) % slides.length;
      const previous = active;
      finish(); active = next;
      slides.forEach((slide, i) => {
        slide.hidden = i !== active;
        slide.inert = i !== active;
        slide.setAttribute('aria-hidden', String(i !== active));
      });
      root.dataset.activeSlide = String(active);
      dots.forEach((dot,i) => dot.setAttribute('aria-pressed', String(i === active)));
      if (status) {
        status.setAttribute('aria-live', manual ? 'polite' : 'off');
        status.textContent = `${String(active+1).padStart(2,'0')} / ${String(slides.length).padStart(2,'0')}`;
      }
      if (animate && previous !== active && !reduced.matches) {
        const outgoing = slides[previous], incoming = slides[active];
        outgoing.hidden = false; outgoing.style.zIndex = '0'; incoming.style.zIndex = '1';
        animations.push(outgoing.animate([{opacity:1},{opacity:0}],{duration:600,easing:'ease-in-out',fill:'forwards'}));
        animations.push(incoming.animate([{opacity:0},{opacity:1}],{duration:600,easing:'ease-in-out',fill:'forwards'}));
        cleanupTimer = setTimeout(finish, 610);
      }
    };
    const auto = rotation(root, () => show(active+1), () => slides.length, finish);
    const manual = index => { show(index,true,true); auto.reset(); };
    root.addEventListener('click', e => {
      const button = e.target.closest('button'); if (!button) return;
      if (button.hasAttribute('data-slide-to')) manual(Number(button.dataset.slideTo));
      if (button.hasAttribute('data-slide-next')) manual(active+1);
      if (button.hasAttribute('data-slide-prev')) manual(active-1);
    }, {signal:auto.signal});
    root.addEventListener('keydown', e => {
      if (!['ArrowLeft','ArrowRight'].includes(e.key) || e.target.matches('input,textarea,select')) return;
      e.preventDefault(); manual(active + (e.key === 'ArrowRight' ? 1 : -1));
    }, {signal:auto.signal});
    root.addEventListener('touchstart', e => { touchX=e.changedTouches[0].screenX; }, {passive:true,signal:auto.signal});
    root.addEventListener('touchend', e => { if(touchX!==null) {const diff=e.changedTouches[0].screenX-touchX;if(Math.abs(diff)>65)manual(active+(diff<0?1:-1));touchX=null;} }, {passive:true,signal:auto.signal});
    root.addEventListener('shopify:block:select', () => auto.pause(), {signal:auto.signal});
    show(0,false);
    return auto.destroy;
  }

  function reviews(root) {
    const items = [...root.querySelectorAll('[data-review-slide]')];
    if (!items.length) return () => {};
    const viewport = root.querySelector('[data-review-viewport]');
    const status = root.querySelector('[data-review-status]');
    const perPage = () => Math.min(items.length, innerWidth < 750 ? 1 : innerWidth < 1050 ? 2 : 3);
    let active = 0, animation = null, size = perPage(), touchX = null;
    const show = (index, animate = true, manual = false) => {
      active = (index + items.length) % items.length;
      size = perPage();
      const visible = Array.from({length:size},(_,i)=>(active+i)%items.length);
      items.forEach((item,i) => { item.hidden = !visible.includes(i); item.style.order = String(visible.indexOf(i)); item.setAttribute('aria-hidden',String(item.hidden)); });
      viewport.style.setProperty('--am-reviews-columns',String(size));
      root.dataset.activeReview = String(active);
      if(status){status.setAttribute('aria-live',manual?'polite':'off');status.textContent=`${visible.map(i=>i+1).join(' · ')} / ${items.length}`;}
      animation?.cancel();
      if (animate && !reduced.matches) animation = viewport.animate([{opacity:0,transform:'translateX(8px)'},{opacity:1,transform:'translateX(0)'}],{duration:550,easing:'ease-out'});
    };
    const auto = rotation(root, () => show(active+size), () => Math.ceil(items.length/perPage()), () => animation?.cancel());
    const manual = index => { show(index,true,true); auto.reset(); };
    root.addEventListener('click', e => {if(e.target.closest('[data-review-next]'))manual(active+size);if(e.target.closest('[data-review-prev]'))manual(active-size);}, {signal:auto.signal});
    root.addEventListener('keydown', e => {if(!['ArrowLeft','ArrowRight'].includes(e.key))return;e.preventDefault();manual(active+(e.key==='ArrowRight'?size:-size));}, {signal:auto.signal});
    viewport.addEventListener('touchstart', e => {touchX=e.changedTouches[0].screenX;}, {passive:true,signal:auto.signal});
    viewport.addEventListener('touchend', e => {if(touchX!==null){const diff=e.changedTouches[0].screenX-touchX;if(Math.abs(diff)>65)manual(active+(diff<0?size:-size));touchX=null;}}, {passive:true,signal:auto.signal});
    window.addEventListener('resize', () => {if(size!==perPage()){show(active,false);auto.reset();}}, {signal:auto.signal});
    root.addEventListener('shopify:block:select', e => {const item=e.target.closest('[data-review-slide]');if(item){show(items.indexOf(item),false,true);auto.pause();}}, {signal:auto.signal});
    show(0,false);
    return auto.destroy;
  }

  function init(scope = document) {
    for(const [root,destroy] of controllers){if(!root.isConnected){destroy();controllers.delete(root);}}
    const roots = [...scope.querySelectorAll('[data-am-slider],[data-am-reviews]')];
    if(scope.matches?.('[data-am-slider],[data-am-reviews]'))roots.unshift(scope);
    roots.forEach(root => {if(!controllers.has(root))controllers.set(root,root.hasAttribute('data-am-slider')?hero(root):reviews(root));});
  }
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => {for(const [root,destroy] of controllers){if(e.target.contains(root)){destroy();controllers.delete(root);}}});
  init();
})();
